import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { clinicAPI } from "../../../services/api";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../utils/notificationUtils";

const EmailInviteTab = ({ clinicId, userType, onSuccess, onClose }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isModerator = userType === "CLINIC_MODERATOR";

  const validate = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = t("manageTeam.errors.emailRequired", "Email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t("manageTeam.errors.emailInvalid", "Please enter a valid email");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!clinicId) {
      showErrorToast(t("manageTeam.errors.missingClinic"), { title: t("manageTeam.toasts.setupRequired.title"), duration: 5000 });
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        clinic_id: clinicId,
        email: email.trim(),
        message: message.trim(),
        user_type: userType,
      };

      const response = isModerator
        ? await clinicAPI.inviteModeratorByEmail(data)
        : await clinicAPI.inviteVetByEmail(data);

      if (response?.data?.status === "success") {
        showSuccessToast(
          t("manageTeam.toasts.inviteSuccess.body", { name: email.trim() }),
          { title: t("manageTeam.toasts.inviteSuccess.title"), duration: 4000 }
        );
        setEmail("");
        setMessage("");
        onSuccess?.();
        onClose?.();
      } else {
        throw new Error(response?.data?.message || t("manageTeam.errors.inviteFailed"));
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || t("manageTeam.errors.inviteFailed");
      showErrorToast(message, { title: t("manageTeam.toasts.inviteFailed.title"), duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="team-email-invite" dir={i18n.dir()} lang={i18n.language}>
      <div className="team-form-group">
        <label className="team-form-label">
          {isModerator
            ? t("manageTeam.invite.moderatorEmailLabel", "Moderator Email")
            : t("manageTeam.invite.vetEmailLabel", "Veterinarian Email")}
        </label>
        <input
          type="email"
          className="team-form-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          disabled={isLoading}
        />
        {errors.email && <div className="team-form-error">{errors.email}</div>}
      </div>

      <div className="team-form-group">
        <label className="team-form-label">{t("manageTeam.invite.modal.messageLabel", "Invitation Message")}</label>
        <textarea
          className="team-form-textarea"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("manageTeam.invite.messagePlaceholder", "Enter a personal message...")}
          disabled={isLoading}
        />
      </div>

      <div className="team-modal-actions">
        <button type="submit" className="team-btn team-btn-primary" disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="team-spinner" style={{ width: "16px", height: "16px", borderWidth: "2px" }} />
              {t("manageTeam.invite.modal.sending", "Sending...")}
            </>
          ) : (
            t("manageTeam.invite.modal.send", "Send Invitation")
          )}
        </button>
      </div>
    </form>
  );
};

export default EmailInviteTab;
